import { Layers, Pause, Pencil, Play, Plus, Settings2, Trash2, Unlink } from 'lucide-react'
import { useState, type CSSProperties } from 'react'
import { toast } from 'sonner'

import { useChannels } from '@/api/endpoints/channels'
import { useRenamePool, useUnlinkChannel, type Pool } from '@/api/endpoints/pools'
import {
  usePatchChannelSchedule,
  useRunChannelGeneration,
  useSchedulerChannels,
  useSchedulerConfig,
} from '@/api/endpoints/scheduler'
import { IconButton } from '@/components/ui/IconButton'
import { Button } from '@/components/ui/Button'
import { DialogContent, DialogRoot } from '@/components/ui/Dialog'
import { Input } from '@/components/ui/Input'
import {
  SelectContent,
  SelectItem,
  SelectRoot,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select'
import { Spinner } from '@/components/ui/Spinner'
import { DeletePoolDialog } from '@/features/pools/components/DeletePoolDialog'
import { LinkPoolDialog } from '@/features/pools/components/LinkPoolDialog'
import { ChannelEditDialog } from '@/features/scheduler/components/ChannelEditDialog'
import { dayLabel, factoryAccent, initials } from '@/features/scheduler/channelCards'
import { buildPoolCards, languageLabel } from '@/features/scheduler/poolCards'
import { cn } from '@/lib/cn'
import { errorMessage } from '@/lib/errors'
import { formatElapsed, formatNumber, formatRelativeDate } from '@/lib/format'
import styles from '@/features/pools/pages/PoolsPage/PoolsPage.module.scss'

interface PoolDetailProps {
  pool: Pool
  factoryLabel: string
  now: Date
}

function when(iso: string, now: Date): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return `${dayLabel(d, now)} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

/** A foto do canal na tabela, com as iniciais quando falta ou não carrega. */
function MemberAvatar({ url, name }: { url: string | null | undefined; name: string }) {
  const [broken, setBroken] = useState(false)
  if (url && !broken) {
    return (
      <img
        className={styles.memberAvatar}
        src={url}
        alt=""
        loading="lazy"
        referrerPolicy="no-referrer"
        onError={() => {
          setBroken(true)
        }}
      />
    )
  }
  return (
    <span className={styles.memberAvatar} aria-hidden>
      {initials(name)}
    </span>
  )
}

/** O detalhe de um pool: estoque, canais ligados, pedidos sem vídeo e ações. */
export function PoolDetail({ pool, factoryLabel, now }: PoolDetailProps) {
  const [card] = buildPoolCards([pool], new Map([[pool.factory, factoryLabel]]), now)
  const { data: config } = useSchedulerConfig()
  const { data: states = {} } = useSchedulerChannels()
  const { data: channels = [] } = useChannels()
  const runGen = useRunChannelGeneration()
  const rename = useRenamePool()
  const unlink = useUnlinkChannel()
  const [editOpen, setEditOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [renameOpen, setRenameOpen] = useState(false)
  const [newName, setNewName] = useState(pool.name)
  const [addOpen, setAddOpen] = useState(false)
  const [candidate, setCandidate] = useState('')
  const [linkOpen, setLinkOpen] = useState(false)
  const [unlinkTarget, setUnlinkTarget] = useState<{ slug: string; name: string } | null>(null)
  // Canal do pool aberto no mesmo modal de edição do Scheduler.
  const [memberEdit, setMemberEdit] = useState<string | null>(null)

  const poolConfig = config?.channels[pool.slug] ?? null
  const memberSlugs = new Set(pool.members.map((m) => m.slug))
  // Canais que podem entrar: do YouTube, fora de qualquer pool.
  const candidates = channels.filter(
    (c) =>
      c.platform === 'youtube' && !memberSlugs.has(c.slug) && !(states[c.slug]?.pool_slug ?? null),
  )
  const candidateName = channels.find((c) => c.slug === candidate)?.name ?? candidate
  const memberEditConfig = memberEdit ? (config?.channels[memberEdit] ?? null) : null
  const memberEditName = pool.members.find((m) => m.slug === memberEdit)?.name ?? memberEdit ?? ''
  // Quem mais rende em cima; empate fica na ordem do backend (por slug).
  const members = [...pool.members].sort((a, b) => b.views - a.views)
  // Ausente no backend de antes da 0095, como ``views_24h``.
  const partial24h = (pool.views_24h_partial as boolean | undefined) ?? false

  return (
    <section
      className={styles.detail}
      role="tabpanel"
      id="pool-panel"
      aria-labelledby={`pool-tab-${pool.slug}`}
      style={{ '--accent': factoryAccent(pool.factory) } as CSSProperties}
    >
      <div className={styles.detailHead}>
        <span className={styles.tile} aria-hidden>
          <Layers />
        </span>
        <div className={styles.detailTitle}>
          <h2 id="pool-name">{pool.name}</h2>
          <div className={styles.tags}>
            <span className={styles.factory}>{factoryLabel}</span>
            <span className={styles.lang}>{languageLabel(pool.language)}</span>
            <button
              type="button"
              className={styles.iconLink}
              aria-label="Renomear o pool"
              onClick={() => {
                setNewName(pool.name)
                setRenameOpen(true)
              }}
            >
              <Pencil aria-hidden />
            </button>
          </div>
        </div>
        <div className={styles.detailActions}>
          <Button
            variant="secondary"
            leadingIcon={<Play />}
            disabled={pool.members.length === 0 || runGen.isPending}
            title={pool.members.length === 0 ? 'Ligue um canal para gerar' : undefined}
            onClick={() => {
              runGen.mutate(pool.slug, {
                onSuccess: () => toast.success(`Geração disparada — ${pool.name}`),
                onError: (err) => toast.error(errorMessage(err, 'Não foi possível gerar')),
              })
            }}
          >
            Gerar agora
          </Button>
          <Button
            variant="secondary"
            disabled={!poolConfig}
            onClick={() => {
              setEditOpen(true)
            }}
          >
            Editar geração
          </Button>
          <Button
            variant="danger"
            leadingIcon={<Trash2 />}
            onClick={() => {
              setDeleteOpen(true)
            }}
          >
            Apagar pool
          </Button>
        </div>
      </div>

      {card ? (
        <div className={styles.stats}>
          <div className={styles.stat}>
            <span className={styles.label}>Disponíveis</span>
            <span className={styles.num}>{pool.available}</span>
            <span className={styles.muted}>prontos para qualquer canal</span>
          </div>
          <div className={styles.stat}>
            <span className={styles.label}>Estoque</span>
            <span className={cn(styles.num, styles[`level-${card.stock}`])}>{card.stockText}</span>
            <span className={styles.muted}>{card.productionText}</span>
          </div>
          <div className={styles.stat}>
            <span className={styles.label}>Publicados</span>
            <span className={styles.num}>{pool.published}</span>
            <span className={styles.muted}>pelos canais do pool</span>
          </div>
          <div className={styles.stat}>
            <span className={styles.label}>Visualizações</span>
            <span className={styles.num}>{formatNumber(pool.views)}</span>
            <span className={styles.muted}>
              dos canais do pool
              <Views24h value={views24h(pool.views_24h)} partial={partial24h} />
            </span>
          </div>
          <div className={styles.stat}>
            <span className={styles.label}>Geração</span>
            <span className={styles.num}>{pool.generation_time || '—'}</span>
            <span className={styles.muted}>{pool.enabled ? 'todo dia' : 'pausada'}</span>
          </div>
        </div>
      ) : null}

      <div className={styles.block}>
        <div className={styles.blockHead}>
          <h3>Canais ligados</h3>
          <Button
            variant="ghost"
            size="sm"
            leadingIcon={<Plus />}
            disabled={candidates.length === 0}
            title={candidates.length === 0 ? 'Nenhum canal do YouTube fora de pool' : undefined}
            onClick={() => {
              setCandidate('')
              setAddOpen(true)
            }}
          >
            Ligar canal
          </Button>
        </div>
        {pool.members.length === 0 ? (
          <p className={styles.muted}>Nenhum canal ainda. Sem canal ligado, o pool não gera.</p>
        ) : (
          <div className={styles.tableBox}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th scope="col">Canal</th>
                  <th scope="col">Uploads/dia</th>
                  <th scope="col">Publicados</th>
                  <th scope="col">Visualizações</th>
                  <th scope="col">Publica há</th>
                  <th scope="col">
                    <span className={styles.srOnly}>Ações</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {members.map((m) => {
                  // O backend antigo não manda ``enabled`` (o front sai antes do pull): ativo.
                  const enabled = (m.enabled as boolean | undefined) ?? true
                  // Ausente no backend de antes da 0095.
                  const memberPartial = (m.views_24h_partial as boolean | undefined) ?? false
                  return (
                    <tr key={m.slug} className={enabled ? undefined : styles.memberPaused}>
                      <td>
                        <span className={styles.memberCell}>
                          <MemberAvatar url={m.avatar_url} name={m.name} />
                          {m.name}
                          {enabled ? null : <span className={styles.pausedTag}>Pausado</span>}
                        </span>
                      </td>
                      <td>{m.uploads_per_day}</td>
                      <td>{formatNumber(m.published)}</td>
                      <td>
                        {formatNumber(m.views)}
                        <Views24h value={views24h(m.views_24h)} partial={memberPartial} />
                      </td>
                      <td>
                        {m.first_published_at ? (
                          <span
                            title={`Primeiro vídeo em ${formatRelativeDate(m.first_published_at)}`}
                          >
                            {formatElapsed(m.first_published_at, now)}
                          </span>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className={styles.right}>
                        <MemberActions
                          slug={m.slug}
                          name={m.name}
                          enabled={enabled}
                          canEdit={Boolean(config?.channels[m.slug])}
                          onEdit={() => {
                            setMemberEdit(m.slug)
                          }}
                          onRemove={() => {
                            setUnlinkTarget({ slug: m.slug, name: m.name })
                          }}
                        />
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {(pool.stockouts ?? []).length > 0 ? (
        <div className={styles.block}>
          <h3>Pedidos sem vídeo</h3>
          <ul className={styles.log}>
            {(pool.stockouts ?? []).map((s) => (
              <li key={`${s.occurred_at}-${s.channel_slug}`}>
                <strong>{when(s.occurred_at, now)}</strong>
                {pool.members.find((m) => m.slug === s.channel_slug)?.name ?? s.channel_slug}{' '}
                {s.reason === 'empty'
                  ? 'pediu vídeo e o pool estava vazio.'
                  : `pediu vídeo: havia ${String(s.available)}, mas nenhum serviu ao canal.`}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {poolConfig ? (
        <ChannelEditDialog
          open={editOpen}
          onClose={() => {
            setEditOpen(false)
          }}
          slug={pool.slug}
          config={poolConfig}
          name={pool.name}
          poolMode
        />
      ) : null}

      {memberEditConfig ? (
        <ChannelEditDialog
          open={memberEdit !== null}
          onClose={() => {
            setMemberEdit(null)
          }}
          slug={memberEdit ?? ''}
          config={memberEditConfig}
          name={memberEditName}
          platform={channels.find((c) => c.slug === memberEdit)?.platform ?? 'youtube'}
          state={memberEdit ? states[memberEdit] : undefined}
        />
      ) : null}

      <DeletePoolDialog
        open={deleteOpen}
        onClose={() => {
          setDeleteOpen(false)
        }}
        slug={pool.slug}
        name={pool.name}
      />

      <DialogRoot open={renameOpen} onOpenChange={setRenameOpen}>
        <DialogContent
          className={styles.formDialog}
          title="Renomear o pool"
          footer={
            <>
              <Button
                variant="secondary"
                onClick={() => {
                  setRenameOpen(false)
                }}
              >
                Cancelar
              </Button>
              <Button
                disabled={!newName.trim() || rename.isPending}
                leadingIcon={rename.isPending ? <Spinner /> : undefined}
                onClick={() => {
                  rename.mutate(
                    { slug: pool.slug, name: newName.trim() },
                    {
                      onSuccess: () => {
                        setRenameOpen(false)
                      },
                      onError: (err) => toast.error(errorMessage(err, 'Não foi possível renomear')),
                    },
                  )
                }}
              >
                Salvar
              </Button>
            </>
          }
        >
          <div className={styles.field}>
            <label htmlFor="pool-rename">Nome</label>
            <Input
              id="pool-rename"
              value={newName}
              maxLength={80}
              onChange={(e) => {
                setNewName(e.target.value)
              }}
            />
          </div>
        </DialogContent>
      </DialogRoot>

      <DialogRoot open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent
          className={styles.formDialog}
          title={`Ligar um canal ao pool ${pool.name}`}
          description="O canal para de gerar e passa a publicar do pool. A próxima tela mostra o que acontece com os vídeos dele."
          footer={
            <>
              <Button
                variant="secondary"
                onClick={() => {
                  setAddOpen(false)
                }}
              >
                Cancelar
              </Button>
              <Button
                disabled={!candidate}
                onClick={() => {
                  setAddOpen(false)
                  setLinkOpen(true)
                }}
              >
                Continuar
              </Button>
            </>
          }
        >
          <div className={styles.field}>
            <label htmlFor="pool-add-channel">Canal</label>
            <SelectRoot {...(candidate ? { value: candidate } : {})} onValueChange={setCandidate}>
              <SelectTrigger id="pool-add-channel" aria-label="Canal">
                <SelectValue placeholder="Escolha um canal" />
              </SelectTrigger>
              <SelectContent>
                {candidates.map((c) => (
                  <SelectItem key={c.slug} value={c.slug}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </SelectRoot>
          </div>
        </DialogContent>
      </DialogRoot>

      {candidate ? (
        <LinkPoolDialog
          open={linkOpen}
          onClose={() => {
            setLinkOpen(false)
          }}
          poolSlug={pool.slug}
          poolName={pool.name}
          channelSlug={candidate}
          channelName={candidateName}
        />
      ) : null}

      <DialogRoot
        open={unlinkTarget !== null}
        onOpenChange={(next) => {
          if (!next) setUnlinkTarget(null)
        }}
      >
        <DialogContent
          role="alertdialog"
          className={styles.formDialog}
          title={`Remover ${unlinkTarget?.name ?? ''} do pool?`}
          description="O canal volta a gerar sozinho, com uma cópia da configuração de geração deste pool e a marca que tinha antes de entrar. Os vídeos que ele já pegou continuam com ele."
          footer={
            <>
              <Button
                variant="secondary"
                onClick={() => {
                  setUnlinkTarget(null)
                }}
              >
                Cancelar
              </Button>
              <Button
                disabled={unlink.isPending}
                leadingIcon={unlink.isPending ? <Spinner /> : undefined}
                onClick={() => {
                  if (!unlinkTarget) return
                  unlink.mutate(
                    { slug: pool.slug, channel: unlinkTarget.slug },
                    {
                      onSuccess: () => {
                        toast.success(`${unlinkTarget.name} voltou a gerar sozinho`)
                        setUnlinkTarget(null)
                      },
                      onError: (err) =>
                        toast.error(errorMessage(err, 'Não foi possível remover o canal')),
                    },
                  )
                }}
              >
                Remover
              </Button>
            </>
          }
        >
          <span className={styles.muted}>
            Os outros canais do pool continuam publicando dele. Para só parar de publicar, sem sair
            do pool, use Pausar.
          </span>
        </DialogContent>
      </DialogRoot>
    </section>
  )
}

interface MemberActionsProps {
  slug: string
  name: string
  enabled: boolean
  canEdit: boolean
  onEdit: () => void
  onRemove: () => void
}

/**
 * As ações de um canal do pool: editar (o modal do Scheduler), pausar ou
 * retomar a publicação sem sair do pool, e remover do pool. Pausar é o
 * ``enabled`` do canal: o ciclo de upload pula ele e o consumo do pool
 * deixa de contá-lo.
 */
function MemberActions({ slug, name, enabled, canEdit, onEdit, onRemove }: MemberActionsProps) {
  const patch = usePatchChannelSchedule(slug)
  const toggle = () => {
    patch.mutate(
      { enabled: !enabled },
      {
        onSuccess: () => {
          toast.success(
            enabled
              ? `${name} pausado — não publica até ser retomado`
              : `${name} voltou a publicar`,
          )
        },
        onError: (err) =>
          toast.error(
            errorMessage(err, enabled ? 'Não foi possível pausar' : 'Não foi possível retomar'),
          ),
      },
    )
  }
  return (
    <span className={styles.memberActions}>
      <IconButton
        variant="ghost"
        size="sm"
        aria-label={`Editar ${name}`}
        title="Editar"
        disabled={!canEdit}
        onClick={onEdit}
      >
        <Settings2 />
      </IconButton>
      <IconButton
        variant="ghost"
        size="sm"
        aria-label={enabled ? `Pausar ${name}` : `Retomar ${name}`}
        title={enabled ? 'Pausar a publicação' : 'Retomar a publicação'}
        disabled={patch.isPending}
        onClick={toggle}
      >
        {enabled ? <Pause /> : <Play />}
      </IconButton>
      <IconButton
        variant="ghost"
        size="sm"
        aria-label={`Remover ${name} do pool`}
        title="Remover do pool"
        onClick={onRemove}
      >
        <Unlink />
      </IconButton>
    </span>
  )
}

/** O backend de antes da 0095 não manda ``views_24h`` (o Pages publica o front antes do pull). */
function views24h(value: number | undefined): number | null {
  return value ?? null
}

/**
 * "+1.234 em 24h" sob o total. Parcial quando faltou a amostra de 24 h atrás
 * de algum vídeo — o primeiro dia depois do deploy, ou um sync parado.
 */
function Views24h({ value, partial }: { value: number | null; partial: boolean }) {
  if (value === null) return null
  return (
    <span
      className={styles.views24h}
      title={
        partial
          ? 'Parcial: falta a leitura de 24 h atrás de alguns vídeos (o histórico começou a ser gravado há menos de um dia, ou o sync parou).'
          : 'Views que os vídeos ganharam nas últimas 24 h'
      }
    >
      +{formatNumber(value)} em 24h{partial ? '*' : ''}
    </span>
  )
}
